if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.content = this.querySelector('.product-media-modal__content');
        this.dialog = this.querySelector('.product-media-modal__dialog');
        this.zoom = { scale: 1, x: 0, y: 0, pointers: new Map(), moved: false };
        this.createNavigation();
        this.bindInteractions();
        this.bindGalleryOpeners();
      }

      createNavigation() {
        if (!this.dialog || this.dialog.querySelector('[data-pm-nav]')) return;
        const previous = document.createElement('button');
        const next = document.createElement('button');
        previous.type = next.type = 'button';
        previous.className = next.className = 'product-media-modal__nav';
        previous.dataset.pmNav = 'previous';
        next.dataset.pmNav = 'next';
        previous.setAttribute('aria-label', 'Vorheriges Produktbild');
        next.setAttribute('aria-label', 'Nächstes Produktbild');
        previous.innerHTML = '<span aria-hidden="true">‹</span>';
        next.innerHTML = '<span aria-hidden="true">›</span>';
        previous.addEventListener('click', (event) => { event.stopPropagation(); this.changeMedia(-1); });
        next.addEventListener('click', (event) => { event.stopPropagation(); this.changeMedia(1); });
        this.dialog.append(previous, next);
      }

      bindInteractions() {
        if (!this.content || this.dataset.zoomBound === 'true') return;
        this.dataset.zoomBound = 'true';
        this.content.addEventListener('click', (event) => {
          if (event.target === this.content) this.hide();
          const image = event.target.closest('img[data-media-id]');
          if (image && !this.zoom.moved) this.toggleZoom(image);
          this.zoom.moved = false;
        });
        this.content.addEventListener('dblclick', (event) => {
          const image = event.target.closest('img[data-media-id]');
          if (!image) return;
          event.preventDefault();
          this.toggleZoom(image);
        });
        this.content.addEventListener('wheel', (event) => {
          const image = event.target.closest('img[data-media-id]');
          if (!image) return;
          event.preventDefault();
          this.setZoom(image, this.zoom.scale + (event.deltaY < 0 ? 0.25 : -0.25));
        }, { passive: false });
        this.content.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.content.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.content.addEventListener('pointerup', (event) => this.onPointerUp(event));
        this.content.addEventListener('pointercancel', (event) => this.onPointerUp(event));
        this.onKeydown = (event) => {
          if (!this.hasAttribute('open')) return;
          if (event.key === 'ArrowLeft') { event.preventDefault(); this.changeMedia(-1); }
          if (event.key === 'ArrowRight') { event.preventDefault(); this.changeMedia(1); }
        };
        document.addEventListener('keydown', this.onKeydown);
      }

      bindGalleryOpeners() {
        const sectionId = this.id.replace('ProductModal-', '');
        const gallery = document.getElementById('MediaGallery-' + sectionId);
        if (!gallery || gallery.dataset.premiumLightboxBound === 'true') return;
        gallery.dataset.premiumLightboxBound = 'true';
        gallery.querySelectorAll('.product__media-item').forEach((item) => {
          const opener = item.querySelector('.product__media-zoom-lightbox[data-media-id]');
          const image = item.querySelector('img');
          if (!opener || !image) return;
          image.classList.add('product-media-lightbox-trigger');
          image.setAttribute('role', 'button');
          image.tabIndex = 0;
          image.setAttribute('aria-label', 'Bild vergrößern');
          image.addEventListener('click', (event) => {
            if (event.target.closest('button, a, input, label')) return;
            event.preventDefault();
            this.returnFocusTo = image;
            this.show(opener);
          });
          image.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this.returnFocusTo = image;
            this.show(opener);
          });
        });
      }

      mediaItems() {
        return Array.from(this.content?.children || []).filter((item) => item.hasAttribute('data-media-id'));
      }

      activeItem() {
        return this.mediaItems().find((item) => item.classList.contains('active'));
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      hide() {
        const returnFocusTo = this.returnFocusTo;
        this.resetZoom();
        super.hide();
        requestAnimationFrame(() => returnFocusTo?.focus({ preventScroll: true }));
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        const activeMedia = this.mediaItems().find((item) => item.getAttribute('data-media-id') === mediaId);
        if (activeMedia) this.showMedia(activeMedia);
      }

      showMedia(media) {
        this.mediaItems().forEach((item) => item.classList.toggle('active', item === media));
        this.resetZoom();
        media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        if (this.content) this.content.scrollLeft = Math.max(0, media.offsetLeft - (this.content.clientWidth - media.clientWidth) / 2);
        const template = media.querySelector('template');
        if (media.nodeName === 'DEFERRED-MEDIA' && template?.content.querySelector('.js-youtube')) media.loadContent();
        media.querySelectorAll('img[data-media-id]').forEach((image) => image.setAttribute('sizes', '100vw'));
        this.updateNavigation();
      }

      changeMedia(direction) {
        const items = this.mediaItems();
        if (items.length < 2) return;
        const index = Math.max(0, items.indexOf(this.activeItem()));
        this.showMedia(items[(index + direction + items.length) % items.length]);
      }

      updateNavigation() {
        const hasMultiple = this.mediaItems().length > 1;
        this.querySelectorAll('[data-pm-nav]').forEach((button) => { button.hidden = !hasMultiple; });
      }

      onPointerDown(event) {
        const image = event.target.closest('img[data-media-id]');
        if (!image) return;
        image.setPointerCapture?.(event.pointerId);
        this.zoom.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        this.zoom.lastPoint = { x: event.clientX, y: event.clientY };
        this.zoom.moved = false;
        if (this.zoom.pointers.size === 2) {
          this.zoom.pinchDistance = this.pointerDistance();
          this.zoom.pinchScale = this.zoom.scale;
        }
      }

      onPointerMove(event) {
        const image = event.target.closest('img[data-media-id]');
        if (!image || !this.zoom.pointers.has(event.pointerId)) return;
        const previous = this.zoom.pointers.get(event.pointerId);
        this.zoom.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (this.zoom.pointers.size === 2) {
          const distance = this.pointerDistance();
          if (this.zoom.pinchDistance) this.setZoom(image, this.zoom.pinchScale * (distance / this.zoom.pinchDistance));
          event.preventDefault();
          return;
        }
        if (this.zoom.scale <= 1) return;
        const deltaX = event.clientX - previous.x;
        const deltaY = event.clientY - previous.y;
        if (Math.abs(deltaX) + Math.abs(deltaY) > 2) this.zoom.moved = true;
        this.setZoom(image, this.zoom.scale, this.zoom.x + deltaX, this.zoom.y + deltaY);
        event.preventDefault();
      }

      onPointerUp(event) {
        this.zoom.pointers.delete(event.pointerId);
        if (this.zoom.pointers.size < 2) this.zoom.pinchDistance = null;
      }

      pointerDistance() {
        const points = Array.from(this.zoom.pointers.values());
        return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      }

      toggleZoom(image) {
        this.setZoom(image, this.zoom.scale > 1 ? 1 : 2.25);
      }

      setZoom(image, scale, x = this.zoom.x, y = this.zoom.y) {
        const nextScale = Math.max(1, Math.min(4, scale));
        const rect = image.getBoundingClientRect();
        const maxX = Math.max(0, (rect.width * (nextScale - 1)) / 2);
        const maxY = Math.max(0, (rect.height * (nextScale - 1)) / 2);
        this.zoom.scale = nextScale;
        this.zoom.x = Math.max(-maxX, Math.min(maxX, x));
        this.zoom.y = Math.max(-maxY, Math.min(maxY, y));
        image.style.setProperty('--pm-zoom', this.zoom.scale);
        image.style.setProperty('--pm-x', this.zoom.x + 'px');
        image.style.setProperty('--pm-y', this.zoom.y + 'px');
        image.classList.toggle('is-zoomed', this.zoom.scale > 1);
      }

      resetZoom() {
        const image = this.activeItem()?.querySelector('img[data-media-id]') || this.activeItem();
        if (image?.nodeName === 'IMG') {
          image.style.removeProperty('--pm-zoom');
          image.style.removeProperty('--pm-x');
          image.style.removeProperty('--pm-y');
          image.classList.remove('is-zoomed');
        }
        this.zoom.scale = 1;
        this.zoom.x = 0;
        this.zoom.y = 0;
        this.zoom.pointers.clear();
        this.zoom.pinchDistance = null;
      }
    }
  );
}